/**
 * RequestApprovalType Field Renderers (PSGroup + RequestApprovalType-specific)
 *
 * PSGroup (possessive-scope concept shared by RequestApprovalType and PluginFunction):
 * - PossessiveTargetAttribute - reference attribute selector with entity browser
 * - PossessiveActorsAttribute(s) - dependent attribute selector(s), options from tier2 for target type
 * - PossessiveScope - entity type selector (used by both)
 * RequestApprovalType-only:
 * - Type - fixed "Possessive" option dropdown
 * - CompletedApprovalEmail - email template selector dropdown
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Checkbox,
    Select,
    ActionIcon,
    Loader,
    Text,
    Badge,
    MultiSelect,
    Group
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { EntityBrowserDialog } from '../../dialogs';
import { ReferenceAttribute } from '../../../hooks/useReferenceAttributeLoader';
import { entityService } from '../../../services/entityServiceSingleton';
import { EntityTypeSelector } from '../../common/EntityTypeSelector';
import CentralizedSchemaService from '../../../services/CentralizedSchemaService';

export interface PossessiveTargetAttributeFieldProps {
    name: string;
    label: string;
    value: any;
    isRequired: boolean;
    /** When true, disables the control (used for both loading state and tier3 read-only). */
    loading: boolean;
    loadingAllReferenceAttributes: boolean;
    allReferenceAttributes: ReferenceAttribute[];
    formData: Record<string, any>;
    handleFieldChange: (name: string, value: any) => void;
    targetAttributeBrowserOpen: boolean;
    setTargetAttributeBrowserOpen: (open: boolean) => void;
    /** When set, clear this field when the attribute value changes (e.g. PossessiveActorsAttribute when PossessiveTargetAttribute changes). */
    dependentFieldToClear?: string;
    /** Optional helper text when options depend on another field (e.g. "Select PossessiveScope first"). */
    helperTextWhenEmpty?: string;
}

/**
 * PossessiveTargetAttribute field: sync and display. Sync runs in useEffect to avoid setState-during-render (max update depth).
 */
function PossessiveTargetAttributeFieldInner(props: PossessiveTargetAttributeFieldProps): React.ReactElement {
    const {
        name,
        label,
        value,
        isRequired,
        loading,
        loadingAllReferenceAttributes,
        allReferenceAttributes,
        formData,
        handleFieldChange,
        targetAttributeBrowserOpen,
        setTargetAttributeBrowserOpen,
        dependentFieldToClear,
        helperTextWhenEmpty
    } = props;

    const stringValue = (typeof value === 'string' ? value : (value ? String(value) : '')).trim();
    const matchingAttr = allReferenceAttributes.find((attr: ReferenceAttribute) => {
        const attrId = (attr.id || attr.value || '').toString().trim();
        const attrName = (attr.name || attr.label || '').toString().trim();
        return attrId === stringValue || attrId.toLowerCase() === stringValue.toLowerCase() ||
            attrName === stringValue || attrName.toLowerCase() === stringValue.toLowerCase();
    });
    const currentValue = matchingAttr ? (matchingAttr.id || matchingAttr.value) : stringValue;
    const availableOptionIds = allReferenceAttributes.map((attr: ReferenceAttribute) =>
        attr.id || attr.value || ''
    ).filter(Boolean);

    // Sync form value to option id in useEffect to avoid "Maximum update depth exceeded" (no setState during render).
    useEffect(() => {
        if (matchingAttr && formData[name] !== currentValue) {
            handleFieldChange(name, currentValue);
            return;
        }
        if (!loadingAllReferenceAttributes && allReferenceAttributes.length > 0) {
            const displayVal = matchingAttr ? (matchingAttr.id || matchingAttr.value) : stringValue;
            const ids = allReferenceAttributes.map((a: ReferenceAttribute) => a.id || a.value || '').filter(Boolean);
            if (displayVal && !ids.includes(displayVal) && formData[name]) {
                handleFieldChange(name, '');
            }
        }
    }, [matchingAttr, currentValue, formData[name], loadingAllReferenceAttributes, allReferenceAttributes.length, name, handleFieldChange]);

    let displayValue = currentValue;
    if (loadingAllReferenceAttributes) {
        if (displayValue && !availableOptionIds.includes(displayValue)) displayValue = '';
    } else if (allReferenceAttributes.length > 0) {
        if (displayValue && !availableOptionIds.includes(displayValue)) {
            console.warn(`⚠️ PossessiveTargetAttribute value "${displayValue}" not found in available options.`);
            displayValue = '';
        }
    } else {
        displayValue = '';
    }

    // Handle search dialog confirm
    const handleTargetAttributeBrowserConfirm = (selectedEntities: Array<{ Id: string; DisplayName: string; EntityType: string }>) => {
        if (selectedEntities.length > 0) {
            const selectedId = selectedEntities[0].Id;
            const selectedAttr = allReferenceAttributes.find((attr: ReferenceAttribute) => {
                const attrId = (attr.id || attr.value || '').toString().trim();
                const sid = (selectedId ?? '').toString().trim();
                return attrId === sid || attrId.toLowerCase() === sid.toLowerCase();
            });
            if (selectedAttr) {
                const attrId = selectedAttr.id || selectedAttr.value || '';
                const previousTarget = formData[name];
                handleFieldChange(name, attrId);
                if (dependentFieldToClear && previousTarget && previousTarget !== attrId) {
                    handleFieldChange(dependentFieldToClear, '');
                }
            }
        }
        setTargetAttributeBrowserOpen(false);
    };

    // Prepare entities for browser dialog
    const browserEntities = allReferenceAttributes.map((attr: ReferenceAttribute) => {
        const attrId = attr.id || attr.value || '';
        const attrLabel = attr.label || attr.name || attrId;
        const entityList = attr.entities || [];
        const entityText = entityList.length > 0 ? ` (${entityList.join(', ')})` : '';
        const isReferenceToList = attr.isReferenceTo || [];
        const isReferenceToText = isReferenceToList.length > 0
            ? ` Refs: ${isReferenceToList.map((ref: any) => typeof ref === 'string' ? ref : (ref.DisplayName || ref.Id || ref)).filter(Boolean).join(', ')}`
            : '';
        return {
            Id: attrId,
            DisplayName: `${attrLabel}${entityText}${isReferenceToText}`,
            Name: attrLabel,
            Type: 'SchemaAttribute'
        };
    });

    const selectData = allReferenceAttributes
        .sort((a, b) => (a.name || a.label || '').localeCompare(b.name || b.label || ''))
        .map((attr: ReferenceAttribute) => {
            const attrId = attr.id || attr.value || '';
            const attrLabel = attr.label || attr.name || attrId;
            const entityList = attr.entities || [];
            const entityText = entityList.length > 0 ? ` (${entityList.join(', ')})` : '';
            const isReferenceToList = attr.isReferenceTo || [];
            const isReferenceToText = isReferenceToList.length > 0
                ? ` Refs: ${isReferenceToList.map((ref: any) => typeof ref === 'string' ? ref : (ref.DisplayName || ref.Id || ref)).filter(Boolean).join(', ')}`
                : '';
            return { value: attrId, label: `${attrLabel}${entityText}${isReferenceToText}` };
        });

    return (
        <Box key={name} mb="md">
            <Group align="flex-start" gap="xs">
                <Select
                    label={label}
                    required={isRequired}
                    value={displayValue}
                    onChange={(v) => {
                        const selectedId = v ?? '';
                        const previousTarget = formData[name];
                        handleFieldChange(name, selectedId);
                        if (dependentFieldToClear && previousTarget && previousTarget !== selectedId) {
                            handleFieldChange(dependentFieldToClear, '');
                        }
                    }}
                    disabled={loading || loadingAllReferenceAttributes}
                    placeholder="Select a reference attribute..."
                    data={loadingAllReferenceAttributes ? [] : allReferenceAttributes.length === 0 ? [] : selectData}
                    leftSection={loadingAllReferenceAttributes ? <Loader size={16} /> : undefined}
                    nothingFoundMessage={helperTextWhenEmpty ?? 'No reference attributes found'}
                    flex={1}
                />
                <ActionIcon
                    variant="subtle"
                    onClick={() => setTargetAttributeBrowserOpen(true)}
                    disabled={loading || loadingAllReferenceAttributes || allReferenceAttributes.length === 0}
                    mt="xs"
                    title="Search attributes"
                >
                    {loadingAllReferenceAttributes ? <Loader size={16} /> : <IconSearch size={16} />}
                </ActionIcon>
            </Group>
            <EntityBrowserDialog
                open={targetAttributeBrowserOpen}
                onClose={() => setTargetAttributeBrowserOpen(false)}
                onConfirm={handleTargetAttributeBrowserConfirm}
                entityType="SchemaAttribute"
                title="Select Reference Attribute"
                callerName="RequestApprovalTypeFieldRenderers (TargetAttribute)"
                preloadedEntities={browserEntities}
                singleSelection={true}
            />
        </Box>
    );
}

export const renderPossessiveTargetAttributeField = (props: PossessiveTargetAttributeFieldProps): React.ReactNode =>
    <PossessiveTargetAttributeFieldInner {...props} />;

export interface PossessiveActorsAttributeFieldProps {
    name: string;
    label: string;
    value: any;
    isRequired: boolean;
    loading: boolean;
    loadingReferenceAttributes: boolean;
    targetEntityTypeReferenceAttributes: any[];
    selectedReferenceAttributeInfo: any;
    formData: Record<string, any>;
    handleFieldChange: (name: string, value: any) => void;
    actorsAttributeBrowserOpen: boolean;
    setActorsAttributeBrowserOpen: (open: boolean) => void;
}

export interface PossessiveActorsAttributesFieldProps {
    name: string;
    label: string;
    value: any;
    isRequired: boolean;
    loading: boolean;
    formData: Record<string, any>;
    handleFieldChange: (name: string, value: any) => void;
    /** When set, options come from tier2 for the entity type that PossessiveTargetAttribute references (after target attribute is selected). */
    attributesFromTier2?: Array<{ name: string; label: string }> | null;
    loadingFromTier2?: boolean;
}

/**
 * Render PossessiveActorsAttribute field (PSGroup: RequestApprovalType)
 */
export const renderPossessiveActorsAttributeField = ({
    name,
    label,
    value,
    isRequired,
    loading,
    loadingReferenceAttributes,
    targetEntityTypeReferenceAttributes,
    selectedReferenceAttributeInfo,
    formData,
    handleFieldChange,
    actorsAttributeBrowserOpen,
    setActorsAttributeBrowserOpen
}: PossessiveActorsAttributeFieldProps): React.ReactNode => {
    // Get display name of selected target attribute
    const targetAttrValue = formData.PossessiveTargetAttribute;
    const targetAttrName = typeof targetAttrValue === 'string' ? targetAttrValue.trim() : '';

    const valueStr = (typeof value === 'string' ? value : '').trim();

    let selectedValue = '';
    let valueIsValid = true;

    if (valueStr) {
        const matchingOption = targetEntityTypeReferenceAttributes.find((attr: any) => {
            const attrId = (attr.id || attr.value || '').toString().trim();
            const attrName = (attr.name || attr.label || attr.DisplayName || '').toString().trim();
            return attrId === valueStr || attrId.toLowerCase() === valueStr.toLowerCase() ||
                attrName === valueStr || attrName.toLowerCase() === valueStr.toLowerCase();
        });

        if (matchingOption) {
            const matchedId = matchingOption.id || matchingOption.value || '';
            selectedValue = matchedId;
            if (formData.PossessiveActorsAttribute !== matchedId) {
                handleFieldChange(name, matchedId);
            }
        } else {
            console.warn(`⚠️ Could not match PossessiveActorsAttribute "${valueStr}" to any available option.`);
            valueIsValid = false;
            selectedValue = valueStr; // Keep the value to show as disabled option
        }
    }

    // Handle search dialog confirm
    const handleActorsAttributeBrowserConfirm = (selectedEntities: Array<{ Id: string; DisplayName: string; EntityType: string }>) => {
        if (selectedEntities.length > 0) {
            const selectedId = selectedEntities[0].Id;
            const selectedAttr = targetEntityTypeReferenceAttributes.find((attr: any) => {
                const attrId = (attr.id || attr.value || '').toString().trim();
                const sid = (selectedId ?? '').toString().trim();
                return attrId === sid || attrId.toLowerCase() === sid.toLowerCase();
            });
            if (selectedAttr) {
                const attrId = selectedAttr.id || selectedAttr.value || '';
                handleFieldChange(name, attrId);
            }
        }
        setActorsAttributeBrowserOpen(false);
    };

    // Prepare entities for browser dialog
    const actorsBrowserEntities = targetEntityTypeReferenceAttributes.map((attr: any) => {
        const attrId = attr.id || attr.value || '';
        const attrLabel = attr.label || attr.name || attr.DisplayName || attrId;
        return {
            Id: attrId,
            DisplayName: attrLabel,
            Name: attrLabel,
            Type: 'SchemaAttribute'
        };
    });

    const selectData = targetEntityTypeReferenceAttributes.map((attr: any) => {
        const attrId = attr.id || attr.value || '';
        const attrLabel = attr.label || attr.name || attr.DisplayName || attrId;
        return { value: attrId, label: attrLabel };
    });

    return (
        <Box key={name} mb="md">
            <Group align="flex-start" gap="xs">
                <Select
                    label={label}
                    required={isRequired}
                    value={selectedValue}
                    onChange={(v) => handleFieldChange(name, v ?? '')}
                    disabled={loading}
                    placeholder="Select a reference attribute..."
                    data={selectData}
                    leftSection={loadingReferenceAttributes ? <Loader size={16} /> : undefined}
                    flex={1}
                />
                <ActionIcon
                    variant="subtle"
                    onClick={() => setActorsAttributeBrowserOpen(true)}
                    disabled={loading || loadingReferenceAttributes || targetEntityTypeReferenceAttributes.length === 0 || !formData.PossessiveTargetAttribute}
                    mt="xs"
                    title="Search attributes"
                >
                    {loadingReferenceAttributes ? <Loader size={16} /> : <IconSearch size={16} />}
                </ActionIcon>
            </Group>
            {selectedValue && !valueIsValid && (
                <Text size="xs" c="yellow" mt="xs">
                    Warning: The selected value "{selectedValue}" is not available. Please select a different value.
                </Text>
            )}
            {formData.PossessiveTargetAttribute && selectedReferenceAttributeInfo && (
                <Text size="xs" c="dimmed" mt="xs">
                    Reference attributes from entity types referenced by {targetAttrName}
                </Text>
            )}
            <EntityBrowserDialog
                open={actorsAttributeBrowserOpen}
                onClose={() => setActorsAttributeBrowserOpen(false)}
                onConfirm={handleActorsAttributeBrowserConfirm}
                entityType="SchemaAttribute"
                title="Select Reference Attribute"
                callerName="RequestApprovalTypeFieldRenderers (ActorsAttribute)"
                preloadedEntities={actorsBrowserEntities}
                singleSelection={true}
            />
        </Box>
    );
};

export interface TypeFieldProps {
    name: string;
    label: string;
    value: any;
    loading: boolean;
    handleFieldChange: (name: string, value: any) => void;
    /** When provided (e.g. from tier3 entitlements), use these for the dropdown; otherwise fall back to default "Possessive". */
    allowedValues?: Array<string | { value?: string; label?: string; Id?: string; DisplayName?: string }>;
    required?: boolean;
}

/**
 * Render Type field for RequestApprovalType. Uses tier3 AllowedValues when provided; otherwise defaults to "Possessive".
 */
export const renderTypeField = ({
    name,
    label,
    value,
    loading,
    handleFieldChange,
    allowedValues: allowedValuesProp,
    required = true
}: TypeFieldProps): React.ReactNode => {
    const options = Array.isArray(allowedValuesProp) && allowedValuesProp.length > 0
        ? allowedValuesProp.map((v: any) => {
            if (typeof v === 'string') return { value: v, label: v };
            return {
                value: v?.value ?? v?.Id ?? v?.DisplayName ?? String(v),
                label: v?.label ?? v?.DisplayName ?? v?.value ?? String(v)
            };
        })
        : [{ value: 'Possessive', label: 'Possessive' }];
    const selectData = [{ value: '', label: '(none)' }, ...options];
    return (
        <Select
            key={name}
            label={label}
            value={value || ''}
            onChange={(v) => handleFieldChange(name, v ?? '')}
            disabled={loading}
            required={required}
            data={selectData}
            mb="md"
        />
    );
};

export interface CompletedApprovalEmailFieldProps {
    name: string;
    label: string;
    value: any;
    isRequired: boolean;
    loading: boolean;
    handleFieldChange: (name: string, value: any) => void;
    /** When provided by parent (e.g. GenericEntityUpsertDialog), use this list and do not fetch; avoids N duplicate calls for 4 email template fields. */
    emailTemplates?: any[];
    loadingTemplates?: boolean;
}

/**
 * CompletedApprovalEmail field component for RequestApprovalType (email template selector)
 * Uses centralized emailTemplates/loadingTemplates from parent when provided; otherwise loads once per instance.
 */
export const CompletedApprovalEmailField: React.FC<CompletedApprovalEmailFieldProps> = ({
    name,
    label,
    value,
    isRequired,
    loading,
    handleFieldChange,
    emailTemplates: emailTemplatesFromParent,
    loadingTemplates: loadingTemplatesFromParent
}) => {
    const [emailTemplatesLocal, setEmailTemplatesLocal] = useState<any[]>([]);
    const [loadingTemplatesLocal, setLoadingTemplatesLocal] = useState(false);
    const [browserOpen, setBrowserOpen] = useState(false);

    const useCentralized = emailTemplatesFromParent !== undefined;
    const emailTemplates = useCentralized ? emailTemplatesFromParent : emailTemplatesLocal;
    const loadingTemplates = useCentralized ? (loadingTemplatesFromParent ?? false) : loadingTemplatesLocal;

    // Load email templates only when not using centralized list from parent
    useEffect(() => {
        if (useCentralized) return;
        const loadEmailTemplates = async () => {
            setLoadingTemplatesLocal(true);
            try {
                let allTemplates: any[] = [];
                let currentPage = 1;
                let hasMore = true;
                const pageSize = 100;

                while (hasMore) {
                    const response = await entityService.getEntitiesByType('EmailTemplate', currentPage, pageSize);
                    const pageTemplates = response.data || [];
                    allTemplates = [...allTemplates, ...pageTemplates];

                    const totalResults = response.totalResults || 0;
                    const totalPages = Math.ceil(totalResults / pageSize);
                    hasMore = currentPage < totalPages;
                    currentPage++;
                }

                allTemplates.sort((a, b) => {
                    const nameA = (a.DisplayName || a.Id || '').toLowerCase();
                    const nameB = (b.DisplayName || b.Id || '').toLowerCase();
                    return nameA.localeCompare(nameB);
                });

                setEmailTemplatesLocal(allTemplates);
            } catch (error) {
                console.error('Failed to load email templates:', error);
            } finally {
                setLoadingTemplatesLocal(false);
            }
        };

        loadEmailTemplates();
    }, [entityService, useCentralized]);

    // Handle browser dialog confirm (accepts entities or string[] for compatibility)
    const handleBrowserConfirm = (input: Array<{ Id: string; DisplayName: string; EntityType: string }> | string[]) => {
        if (Array.isArray(input) && input.length > 0) {
            const selectedId = typeof input[0] === 'object' && input[0] !== null && 'Id' in input[0]
                ? (input[0] as { Id: string }).Id
                : (input[0] as string);
            handleFieldChange(name, selectedId);
        }
        setBrowserOpen(false);
    };

    // Prepare entities for browser dialog
    const browserEntities = emailTemplates.map((template: any) => ({
        Id: template.Id,
        DisplayName: template.DisplayName || template.Id,
        Name: template.DisplayName || template.Id,
        Type: 'EmailTemplate'
    }));

    const stringValue = typeof value === 'string' ? value : (value ? String(value) : '');
    const selectedValue = stringValue || '';

    const emailSelectData = emailTemplates.map((template: any) => ({
        value: template.Id,
        label: template.DisplayName || template.Id
    }));

    return (
        <Box key={name} mb="md">
            <Group align="flex-start" gap="xs">
                <Select
                    label={label}
                    required={isRequired}
                    value={selectedValue}
                    onChange={(v) => handleFieldChange(name, v || null)}
                    disabled={loading || loadingTemplates}
                    placeholder="Select an email template..."
                    data={emailSelectData}
                    leftSection={loadingTemplates ? <Loader size={16} /> : undefined}
                    nothingFoundMessage="No email templates found"
                    flex={1}
                />
                <ActionIcon
                    variant="subtle"
                    onClick={() => setBrowserOpen(true)}
                    disabled={loading || loadingTemplates || emailTemplates.length === 0}
                    mt="xs"
                    title="Search email templates"
                >
                    {loadingTemplates ? <Loader size={16} /> : <IconSearch size={16} />}
                </ActionIcon>
            </Group>
            <EntityBrowserDialog
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onConfirm={handleBrowserConfirm}
                entityType="EmailTemplate"
                title="Select Email Template"
                callerName="RequestApprovalTypeFieldRenderers (CompletedApprovalEmail)"
                preloadedEntities={browserEntities}
                singleSelection={true}
            />
        </Box>
    );
};

/**
 * Render CompletedApprovalEmail field for RequestApprovalType (email template selector)
 * This is a wrapper function that returns the component for consistency with other renderers
 */
export const renderCompletedApprovalEmailField = (props: CompletedApprovalEmailFieldProps): React.ReactNode => {
    return <CompletedApprovalEmailField {...props} />;
};

export interface PossessiveScopeFieldProps {
    name: string;
    label: string;
    value: any;
    isRequired: boolean;
    /** When true, disables the control (used for both loading state and tier3 read-only). */
    loading: boolean;
    formData: Record<string, any>;
    handleFieldChange: (name: string, value: any) => void;
    /** Entity types for the dropdown (e.g. from tier1 entitlements). When set, used instead of schema so selector is not empty. */
    entityTypes?: { name: string; id?: string }[] | null;
    /** Entity type names (lowercase) to exclude from the dropdown (e.g. disjointed types). */
    excludeTypes?: string[];
}

/**
 * PossessiveActorsAttributes field component (PSGroup: RequestApprovalType plural, PluginFunction; comma-delimited list of attributes).
 * When attributesFromTier2 is passed: options come ONLY from tier2 for the entity type
 * that PossessiveTargetAttribute references. Nothing until target is selected and tier2 is retrieved.
 * When attributesFromTier2 is not passed (legacy): options from PossessiveScope via schema.
 */
export const PossessiveActorsAttributesField: React.FC<PossessiveActorsAttributesFieldProps> = ({
    name,
    label,
    value,
    isRequired,
    loading,
    formData,
    handleFieldChange,
    attributesFromTier2,
    loadingFromTier2 = false
}) => {
    const [attributes, setAttributes] = useState<Array<{ name: string; label: string }>>([]);
    const [loadingAttributes, setLoadingAttributes] = useState(false);

    // PossessiveTargetAttribute may be string (e.g. "$.School") or object (e.g. { id, label }) from Select (check both key variants)
    const rawTarget = formData['$.PossessiveTargetAttribute'] ?? formData.PossessiveTargetAttribute ?? formData.possessiveTargetAttribute;
    const possessiveTargetAttribute =
        typeof rawTarget === 'string'
            ? rawTarget.trim()
            : rawTarget && typeof rawTarget === 'object'
            ? ((rawTarget as any).id ?? (rawTarget as any).Id ?? (rawTarget as any).value ?? (rawTarget as any).name ?? (rawTarget as any).Name ?? '').toString().trim()
            : '';

    const tier2OnlyMode = attributesFromTier2 !== undefined;
    const useTier2Options = !!possessiveTargetAttribute && tier2OnlyMode;
    // When in tier2-only mode: only show options from tier2 when target is selected; otherwise empty. Never use schema.
    const effectiveAttributes = tier2OnlyMode
        ? (useTier2Options ? (attributesFromTier2 ?? []) : [])
        : (useTier2Options ? (attributesFromTier2 ?? []) : attributes);
    const effectiveLoading = useTier2Options ? loadingFromTier2 : loadingAttributes;

    const possessiveScope = formData.PossessiveScope ?? formData.possessiveScope;

    // Load attributes from PossessiveScope via schema only when NOT in tier2-only mode (legacy path)
    useEffect(() => {
        if (tier2OnlyMode) {
            setAttributes([]);
            return;
        }
        if (!useTier2Options && possessiveScope) {
            setLoadingAttributes(true);
            CentralizedSchemaService.getInstance()
                .getFieldOptionsForEntityType(possessiveScope)
                .then((fieldOptions) => {
                    const mappedAttributes = fieldOptions
                        .map(attr => ({
                            name: attr.name || attr.id || '',
                            label: attr.label || attr.name || attr.id || ''
                        }))
                        .filter(attr => attr.name);
                    setAttributes(mappedAttributes);
                })
                .catch((error) => {
                    console.error('Failed to load attributes for PossessiveScope:', error);
                    setAttributes([]);
                })
                .finally(() => setLoadingAttributes(false));
        } else {
            setAttributes([]);
        }
    }, [tier2OnlyMode, useTier2Options, possessiveScope]);
    
    // Parse comma-delimited value to array
    const valueStr = typeof value === 'string' ? value : (value ? String(value) : '');
    const selectedAttributes = valueStr
        ? valueStr.split(',').map(s => s.trim()).filter(Boolean)
        : [];
    
    // Handle change - convert array back to comma-delimited string
    const handleChange = (newValues: string[]) => {
        const newValue = newValues.join(', ');
        handleFieldChange(name, newValue);
    };
    
    const helperText = tier2OnlyMode
        ? (!possessiveTargetAttribute
            ? "Select PossessiveTargetAttribute first; then options load from tier2 for that type."
            : effectiveLoading
            ? "Loading attributes from tier2…"
            : "Select attributes from the target entity type (comma-delimited)")
        : (!possessiveScope
            ? "Select a PossessiveScope entity type first"
            : effectiveLoading
            ? "Loading attributes..."
            : `Select attributes from ${possessiveScope} (comma-delimited)`);

    // Stable key so we don't remount when options load (which would clear the displayed value on edit)
    const autocompleteKey = tier2OnlyMode ? `${name}-${possessiveTargetAttribute || 'none'}` : `${name}-${possessiveScope || 'none'}`;

    const multiSelectData = effectiveAttributes.map(attr => ({
        value: attr.name,
        label: attr.label
    }));

    return (
        <Box key={name} mb="md">
            <MultiSelect
                key={autocompleteKey}
                label={label}
                required={isRequired}
                description={helperText}
                data={multiSelectData}
                value={selectedAttributes}
                onChange={handleChange}
                disabled={loading || effectiveLoading || (tier2OnlyMode ? !possessiveTargetAttribute : !possessiveScope)}
                searchable
                clearable
                leftSection={effectiveLoading ? <Loader size={16} /> : undefined}
            />
        </Box>
    );
};

/**
 * Render PossessiveActorsAttributes field for PluginFunction (comma-delimited list of attributes from PossessiveScope)
 * This is a wrapper function that returns the component for consistency with other renderers
 */
export const renderPossessiveActorsAttributesField = (props: PossessiveActorsAttributesFieldProps): React.ReactNode => {
    return <PossessiveActorsAttributesField {...props} />;
};

/**
 * Render PossessiveScope field (PSGroup: RequestApprovalType and PluginFunction, entity type selector)
 * Required when Type is "Possessive" (RequestApprovalType)
 */
export const renderPossessiveScopeField = ({
    name,
    label,
    value,
    isRequired,
    loading,
    formData,
    handleFieldChange,
    entityTypes: entityTypesOverride,
    excludeTypes
}: PossessiveScopeFieldProps): React.ReactNode => {
    // Check if Type is "Possessive" to determine if field should be required (RequestApprovalType)
    const typeValue = formData.Type || formData.type || '';
    const isPossessive = typeValue?.toLowerCase() === 'possessive';
    const fieldRequired = isRequired || isPossessive;

    return (
        <Box key={name} mb="md">
            <EntityTypeSelector
                value={value || null}
                onChange={(entityTypeValue) => handleFieldChange(name, entityTypeValue || '')}
                label={label}
                required={fieldRequired}
                disabled={loading}
                allowClear={!fieldRequired}
                entityTypesOverride={entityTypesOverride ?? undefined}
                excludeTypes={excludeTypes}
                helperText={isPossessive 
                    ? "Required: Select the entity type for possessive scope" 
                    : "Select the entity type for possessive scope"}
            />
        </Box>
    );
};

export default {
    renderPossessiveTargetAttributeField,
    renderPossessiveActorsAttributeField,
    renderTypeField,
    renderCompletedApprovalEmailField,
    renderPossessiveScopeField
};

